import { Flex, Icon, Spacer, Text } from '@chakra-ui/react'
import {
  Ban,
  Bell,
  Cable,
  Download,
  FlaskRound,
  Headset,
  Keyboard,
  MessageSquareQuote,
  Phone,
  Printer,
  UserRound,
  Volume2,
} from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useExperimentsState } from '~/atoms/ExperimentsState'
import { useUserState } from '~/atoms/UserState'
import {
  ResponsivePage,
  useTogglePanel,
} from '~/components/layout/ResponsivePage'
import { NotificationBadgeVariant } from '~/components/shared/componentStyles/NotificationBadge.style'
import { NotificationBadge } from '~/components/shared/NotificationBadge'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useAppVersion } from '~/hooks/useAppVersion'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { useScrollToHash } from '~/hooks/useScrollToHash'
import UpdaterButton from '~/pages/Settings/UpdaterButton'
import { AnalyticsNamespace, SharedAnalyticsEvents } from '~/types/Analytics'
import { SettingsType } from '~/types/Settings'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'

import { AudioSettings } from './AudioSettings'
import { BlockedNumbers } from './BlockedNumbers'
import { CallCenterSettings } from './CallCenterSettings'
import {
  useSettingsPageLayoutStyle,
  useSettingsTabStyle,
} from './componentStyles/SettingsPage.style'
import { ExperimentSettings } from './ExperimentSettings'
import { FaxSettings } from './FaxSettings'
import { IntegrationsSettings } from './IntegrationsSettings'
import { NotificationSettings } from './NotificationSettings'
import { PhoneSettings } from './PhoneSettings'
import { ProfileSettings } from './ProfileSettings'
import KeyboardShortcutsDetail from './ShortcutKeysHelp'
import { SnippetsSettings } from './SnippetsSettings'
import { UpdateSettings } from './UpdateSettings'

type SettingOptionValue = SettingsType

type SettingOptionType = {
  label: string
  value: SettingOptionValue
  icon: SVGComponent
  iconHeight?: number
  badge?: string
  hideInMenu?: boolean
  route?: string
}

type SettingsTabItemProps = {
  menu: SettingOptionType
  isSelected: boolean
  onClick: () => void
}

const SettingsTabItem = ({
  menu,
  isSelected,
  onClick,
}: SettingsTabItemProps) => {
  const tabStyles = useSettingsTabStyle()

  return (
    <Flex
      as="button"
      css={tabStyles.container}
      aria-selected={isSelected}
      onClick={onClick}
      title={menu.label}
    >
      <Icon as={menu.icon} aria-selected={isSelected} css={tabStyles.icon} />
      <Text aria-selected={isSelected} css={tabStyles.titleText}>
        {menu.label}
      </Text>
      <Flex css={tabStyles.badgeWrapper}>
        {(menu.badge ?? false) && (
          <NotificationBadge
            count={menu.badge ?? ''}
            size={Size.md}
            variant={NotificationBadgeVariant.New}
            paddingLeft=".5rem"
          />
        )}
      </Flex>
    </Flex>
  )
}

export const SettingsPage = () => {
  const appVersion = useAppVersion()
  const { track } = useAnalytics(AnalyticsNamespace.SHARED)
  const { t } = useTranslation()
  const layoutStyles = useSettingsPageLayoutStyle()
  const { experimentState } = useExperimentsState()
  const navigate = useNavigate()
  const { setting } = useParams()

  const selectedMenu = setting ?? SettingsType.PROFILE

  useScrollToHash()

  const {
    state: { isOpen: isListPanelOpen },
    togglePanelVisibility,
  } = useTogglePanel()

  useEffect(() => {
    if (!isListPanelOpen) {
      togglePanelVisibility(true)
    }
  }, [])

  const { pbxId, currentUserExtensionId, agentStatusesEnabled, userRoles } =
    useUserState()

  const isPortalLimited = userRoles?.roles?.some(
    (role) => role.type === 'Portal Limited',
  )
  const canViewIntegrations =
    userRoles?.is_staff || userRoles?.is_superuser || !isPortalLimited

  const baseSettingsOptions: SettingOptionType[] = [
    {
      label: t('baseSettings.profile'),
      value: SettingsType.PROFILE,
      icon: UserRound,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.snippets'),
      value: SettingsType.SNIPPETS,
      icon: MessageSquareQuote,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.phone'),
      value: SettingsType.PHONE,
      icon: Phone,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.fax'),
      value: SettingsType.FAX,
      icon: Printer,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.notifications'),
      value: SettingsType.NOTIFICATIONS,
      icon: Bell,
    },
    {
      label: t('baseSettings.audio'),
      value: SettingsType.AUDIO,
      icon: Volume2,
    },
    {
      label: t('baseSettings.blockedNumbers'),
      value: SettingsType.BLOCKED_NUMBERS,
      icon: Ban,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.integrations'),
      value: SettingsType.INTEGRATIONS,
      icon: Cable,
      iconHeight: 20,
      badge: t('baseSettings.beta', 'Beta'),
      hideInMenu: !experimentState.integrationsEnabled || !canViewIntegrations,
    },
    {
      label: t('baseSettings.experiments'),
      value: SettingsType.EXPERIMENTS,
      icon: FlaskRound,
      badge: isElectron ? t('baseSettings.new') : '',
    },
    {
      label: t('baseSettings.updates'),
      value: SettingsType.UPDATES,
      icon: Download,
      iconHeight: 20,
      badge: isElectron ? t('baseSettings.new') : '',
      hideInMenu: !isElectron,
    },
    {
      label: t('baseSettings.shortcutKeys'),
      value: SettingsType.SHORTCUTS,
      icon: Keyboard,
    },
  ]

  const callCenterOption = {
    label: t('baseSettings.callCenter'),
    value: SettingsType.CALL_CENTER,
    icon: Headset,
    iconHeight: 20,
  }

  const settingsOptions: SettingOptionType[] = [
    ...baseSettingsOptions.slice(0, 7),
    ...(agentStatusesEnabled ? [callCenterOption] : []),
    ...baseSettingsOptions.slice(7),
  ]

  const handleChangeMenu = (menuItem: SettingOptionType) => {
    track(SharedAnalyticsEvents.SettingsMenuChanged, {
      label: menuItem.label,
      value: menuItem.value,
    })

    togglePanelVisibility(false)

    if (menuItem.route) {
      navigate(menuItem.route)
      return
    }

    navigate(`${ApplicationRoutes.SETTINGS}/${menuItem.value}`)
  }

  const renderSelectedSettingsPage = () => {
    switch (selectedMenu) {
      case SettingsType.PROFILE:
        return <ProfileSettings />
      case SettingsType.SNIPPETS:
        return <SnippetsSettings />
      case SettingsType.PHONE:
        return <PhoneSettings pbxId={pbxId} />
      case SettingsType.EXPERIMENTS:
        return <ExperimentSettings />
      case SettingsType.NOTIFICATIONS:
        return <NotificationSettings />
      case SettingsType.AUDIO:
        return <AudioSettings />
      case SettingsType.CALL_CENTER:
        return (
          <CallCenterSettings
            pbxId={pbxId}
            extensionId={currentUserExtensionId}
          />
        )
      case SettingsType.BLOCKED_NUMBERS:
        return <BlockedNumbers />
      case SettingsType.INTEGRATIONS:
        return <IntegrationsSettings />
      case SettingsType.SHORTCUTS:
        return <KeyboardShortcutsDetail />
      case SettingsType.UPDATES:
        return <UpdateSettings />
      case SettingsType.FAX:
        return <FaxSettings pbxId={pbxId} />
      default:
        break
    }
  }

  const settingsListPanel = (
    <>
      <Flex css={layoutStyles.header}>
        <Text css={layoutStyles.headerText}>{t('nav.settings')}</Text>
      </Flex>
      <Flex css={layoutStyles.tabListWrapper}>
        <Flex css={layoutStyles.tabList}>
          {settingsOptions.map((menu: SettingOptionType, index) => {
            if (menu.hideInMenu) {
              return null
            }

            return (
              <SettingsTabItem
                key={`menu-${menu.label}-${index}`}
                menu={menu}
                isSelected={selectedMenu === menu.value}
                onClick={() => handleChangeMenu(menu)}
              />
            )
          })}

          <Spacer />
          <UpdaterButton />
          <Flex {...layoutStyles.version}>
            <Text {...layoutStyles.versionText}>{`v${appVersion}`}</Text>
          </Flex>
        </Flex>
      </Flex>
    </>
  )

  return (
    <ResponsivePage
      listPanel={settingsListPanel}
      listPanelProps={{ width: 'fit-content', minWidth: '14rem' }}
      detailPanel={renderSelectedSettingsPage()}
    />
  )
}
